/**
 * NotificationBell Component (T056)
 *
 * Bell icon with dropdown menu showing recent notifications.
 * Displays unread count badge and auto-refreshes every 10 seconds.
 *
 * Features:
 * - Bell icon with red badge for unread count
 * - Click opens dropdown with last 5 notifications
 * - Loading state: spinning bell
 * - Error state: bell with X
 * - Auto-refresh using TanStack Query
 * - "View All" link to full notifications page
 *
 * References:
 * - spec.md: FR-014 (Member notifications for role changes)
 * - spec.md: SC-008 (Notifications delivered within 5 seconds)
 * - DC-83: Phase 7 - Member Notifications
 */

import { useState, useRef, useEffect } from 'react';
import { Bell, BellOff, X, Loader2 } from 'lucide-react';
import { useUserNotifications } from '../hooks/useUserNotifications';
import { Link } from 'react-router-dom';
import type { Notification } from '@codemerlin/shared/types/notification';

/**
 * Props for NotificationBell component
 */
export interface NotificationBellProps {
  /**
   * Organization ID to fetch notifications for (required)
   */
  organizationId: string;
}

/**
 * Format timestamp as relative time (e.g., "5 minutes ago")
 *
 * @param date - Date to format
 * @returns Human-readable relative time string
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  } else if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * NotificationBell component
 *
 * Icon component with dropdown menu showing user notifications.
 * Auto-refreshes every 10 seconds to show near-real-time updates.
 *
 * @param props - Component props
 * @returns Bell icon with notification dropdown
 */
export function NotificationBell({ organizationId }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch last 5 notifications with auto-refresh
  const { data, isLoading, error, refetch } = useUserNotifications(organizationId, {
    limit: 5,
    offset: 0,
    enabled: true, // Always enabled for auto-refresh
  });

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = (data?.notifications ?? []) as Notification[];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (!isOpen) {
      return;
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Determine bell icon based on state
  let BellIcon = Bell;
  let iconClassName = 'h-6 w-6 text-gray-700 hover:text-gray-900 cursor-pointer';

  if (isLoading) {
    BellIcon = Loader2;
    iconClassName = 'h-6 w-6 text-gray-500 animate-spin';
  } else if (error) {
    BellIcon = BellOff;
    iconClassName = 'h-6 w-6 text-red-500 hover:text-red-700 cursor-pointer';
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <BellIcon className={iconClassName} />

        {/* Unread Count Badge */}
        {!isLoading && !error && unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Close notifications"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {error ? (
              <div className="px-4 py-8 text-center">
                <BellOff className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Failed to load notifications</p>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Try again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No notifications yet</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {notifications.map((notification: Notification) => (
                  <li
                    key={notification.id}
                    className="px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm text-gray-900">{notification.message}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">by {notification.actorName}</p>
                        <p className="text-xs text-gray-500">
                          {formatRelativeTime(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <Link
                to={`/organization/${organizationId}/notifications`}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={() => setIsOpen(false)}
              >
                View All Notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
